
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import tradingActualDispatchService from "../services/tradingActualDispatchService";

const styles = {
  page: {
    padding: "24px",
    background: "#f7f8fc",
    minHeight: "100%",
    fontFamily: "Inter, Arial, sans-serif",
    color: "#252b3b",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "16px",
    flexWrap: "wrap",
    marginBottom: "22px",
  },
  title: { margin: 0, fontSize: "25px", fontWeight: 700 },
  subtitle: { margin: "7px 0 0", color: "#747b8d", fontSize: "13px" },
  card: {
    background: "#fff",
    border: "1px solid #e8eaf2",
    borderRadius: "12px",
    padding: "20px",
    marginBottom: "18px",
  },
  sectionTitle: { margin: "0 0 18px", fontSize: "16px", fontWeight: 700 },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
    gap: "20px",
  },
  label: {
    color: "#747b8d",
    fontSize: "11px",
    fontWeight: 700,
    textTransform: "uppercase",
    marginBottom: "7px",
    letterSpacing: ".3px",
  },
  value: {
    fontSize: "14px",
    fontWeight: 600,
    overflowWrap: "anywhere",
    whiteSpace: "pre-wrap",
  },
  button: {
    border: 0,
    borderRadius: "8px",
    padding: "11px 17px",
    background: "#6254d9",
    color: "#fff",
    fontWeight: 600,
    cursor: "pointer",
  },
  secondaryButton: {
    border: "1px solid #dfe2eb",
    borderRadius: "8px",
    padding: "10px 14px",
    background: "#fff",
    color: "#374151",
    fontWeight: 600,
    cursor: "pointer",
  },
  badge: {
    display: "inline-block",
    borderRadius: "20px",
    padding: "6px 10px",
    fontSize: "11px",
    fontWeight: 700,
    whiteSpace: "nowrap",
  },
  tableWrap: { width: "100%", overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse", minWidth: "850px" },
  th: {
    textAlign: "left",
    padding: "12px",
    background: "#f7f8fc",
    color: "#70778a",
    fontSize: "11px",
    textTransform: "uppercase",
    borderBottom: "1px solid #e8eaf2",
    whiteSpace: "nowrap",
  },
  td: {
    padding: "13px 12px",
    fontSize: "13px",
    borderBottom: "1px solid #f0f1f6",
    verticalAlign: "top",
  },
  error: {
    padding: "12px 14px",
    background: "#fff0f0",
    color: "#b42318",
    borderRadius: "8px",
    fontSize: "13px",
    marginBottom: "16px",
  },
  info: {
    padding: "12px 14px",
    background: "#f0f5ff",
    color: "#244a91",
    borderRadius: "8px",
    fontSize: "13px",
    marginBottom: "16px",
  },
  muted: { color: "#858b9b", fontSize: "13px" },
  warning: {
    padding: "14px",
    background: "#fff8e8",
    color: "#895900",
    border: "1px solid #f3dfb3",
    borderRadius: "8px",
    fontSize: "13px",
    marginBottom: "16px",
  },
};

const getResponseData = (response) =>
  response?.data?.data ?? response?.data ?? {};

const getErrorMessage = (err, fallback) =>
  err?.response?.data?.message ||
  err?.response?.data?.error ||
  err?.message ||
  fallback;

const formatStatus = (value) =>
  String(value || "UNKNOWN").toUpperCase().replace(/_/g, " ");

const formatDate = (value) => {
  if (!value) return "—";

  const raw = String(value).slice(0, 10);
  const parts = raw.split("-");

  return parts.length === 3
    ? `${parts[2]}-${parts[1]}-${parts[0]}`
    : raw;
};

const formatDateTime = (value) => {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return String(value);

  return date.toLocaleString("en-IN");
};

const formatQuantity = (value) => {
  if (value === null || value === undefined || value === "") return "—";

  const number = Number(value);

  return Number.isFinite(number)
    ? number.toLocaleString("en-IN", { maximumFractionDigits: 3 })
    : "—";
};

const statusColors = {
  DRAFT: { background: "#efedff", color: "#5548cb" },
  CONFIRMED: { background: "#e8f8ee", color: "#187443" },
  CANCELLED: { background: "#edf0f5", color: "#596176" },
};

function Field({ label, value }) {
  return (
    <div>
      <div style={styles.label}>{label}</div>
      <div style={styles.value}>
        {value === null || value === undefined || value === ""
          ? "—"
          : value}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const code = String(status || "UNKNOWN").toUpperCase();

  return (
    <span
      style={{
        ...styles.badge,
        ...(statusColors[code] || {
          background: "#edf0f5",
          color: "#596176",
        }),
      }}
    >
      {formatStatus(code)}
    </span>
  );
}

export default function ActualDispatchDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [dispatch, setDispatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  const loadActualDispatch = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response =
        await tradingActualDispatchService.getActualDispatchDetail(id);

      const body = getResponseData(response);
      const record = body?.actual_dispatch || body;

      if (!record || !record.id) {
        throw new Error(
          "Actual Dispatch details were not returned by the API."
        );
      }

      setDispatch(record);
    } catch (err) {
      setDispatch(null);
      setError(
        getErrorMessage(err, "Unable to load Actual Dispatch details.")
      );
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadActualDispatch();
  }, [loadActualDispatch]);

  const confirmActualDispatch = async () => {
    if (!dispatch || dispatch.status_code !== "DRAFT") return;

    const confirmed = window.confirm(
      `Confirm Actual Dispatch ${dispatch.actual_dispatch_no || dispatch.id}?\n\n` +
      "This will post inventory OUT, update Sales Order dispatched quantities " +
      "and generate the Delivery Challan. This action should not be repeated."
    );

    if (!confirmed) return;

    setActionLoading(true);
    setError("");
    setInfo("");

    try {
      await tradingActualDispatchService.confirmActualDispatch(id);

      setInfo(
        "Actual Dispatch confirmed. Inventory posting and Delivery Challan generation completed."
      );

      await loadActualDispatch();
    } catch (err) {
      setError(
        getErrorMessage(err, "Unable to confirm Actual Dispatch.")
      );
    } finally {
      setActionLoading(false);
    }
  };

  if (loading && !dispatch) {
    return (
      <div style={styles.page}>
        <p style={styles.muted}>Loading Actual Dispatch details...</p>
      </div>
    );
  }

  if (!dispatch) {
    return (
      <div style={styles.page}>
        <div style={styles.header}>
          <h1 style={styles.title}>Actual Dispatch Details</h1>

          <button
            type="button"
            style={styles.secondaryButton}
            onClick={() => navigate("/trading/actual-dispatches")}
          >
            Back to Actual Dispatches
          </button>
        </div>

        {error && (
          <div role="alert" style={styles.error}>
            {error}
          </div>
        )}

        <button
          type="button"
          style={styles.button}
          onClick={loadActualDispatch}
        >
          Retry
        </button>
      </div>
    );
  }

  const status = String(dispatch.status_code || "DRAFT").toUpperCase();
  const isDraft = status === "DRAFT";

  const items = Array.isArray(dispatch.items) ? dispatch.items : [];
  const challan = dispatch.delivery_challan || null;
  const challanItems = Array.isArray(challan?.items)
    ? challan.items
    : [];

  const totalDispatchQuantity = items.reduce(
    (total, item) => total + Number(item.actual_dispatch_quantity || 0),
    0
  );

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>
            {dispatch.actual_dispatch_no ||
              `Actual Dispatch ${dispatch.id}`}
          </h1>

          <p style={styles.subtitle}>
            Actual shipment execution and inventory posting details
          </p>

          <div style={{ marginTop: "10px" }}>
            <StatusBadge status={status} />
          </div>
        </div>

        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
          <button
            type="button"
            style={styles.secondaryButton}
            onClick={() => navigate("/trading/actual-dispatches")}
          >
            Back to List
          </button>

                    <button
            type="button"
            style={styles.secondaryButton}
            onClick={loadActualDispatch}
            disabled={loading || actionLoading}
          >
            Refresh
          </button>

          {isDraft && (
            <>
              <button
                type="button"
                style={styles.secondaryButton}
                onClick={() =>
                  navigate(`/trading/actual-dispatches/${id}/edit`)
                }
                disabled={actionLoading || loading}
              >
                Edit
              </button>

              <button
                type="button"
                style={styles.button}
                onClick={confirmActualDispatch}
                disabled={actionLoading || loading}
              >
                {actionLoading ? "Confirming..." : "Confirm Actual Dispatch"}
              </button>
            </>
          )}
        </div>
      </div>

      {error && (
        <div role="alert" style={styles.error}>
          {error}
        </div>
      )}

      {info && (
        <div role="status" style={styles.info}>
          {info}
        </div>
      )}

      {isDraft && (
        <div style={styles.warning}>
          <strong>Draft Actual Dispatch:</strong> Inventory has not yet been
          posted and no Delivery Challan has been generated. Confirming this
          dispatch will execute the inventory and Sales Order updates.
        </div>
      )}

      <section style={styles.card}>
        <h2 style={styles.sectionTitle}>Actual Dispatch Overview</h2>

        <div style={styles.grid}>
          <Field
            label="Actual Dispatch No."
            value={dispatch.actual_dispatch_no}
          />
          <Field
            label="Actual Dispatch Date"
            value={formatDate(dispatch.actual_dispatch_date)}
          />
          <Field
            label="Dispatch Order No."
            value={dispatch.dispatch_order_no}
          />
          <Field
            label="Dispatch Order ID"
            value={dispatch.dispatch_order_id}
          />
          <Field
            label="Sales Order No."
            value={dispatch.sales_order_no}
          />
          <Field
            label="Sales Order ID"
            value={dispatch.sales_order_id}
          />
          <Field
            label="Customer"
            value={
              dispatch.customer_name ||
              (dispatch.customer_id
                ? `Customer #${dispatch.customer_id}`
                : null)
            }
          />
          <Field
            label="Customer ID"
            value={dispatch.customer_id}
          />
          <Field
            label="Total Items"
            value={items.length}
          />
          <Field
            label="Total Dispatch Quantity"
            value={formatQuantity(totalDispatchQuantity)}
          />
          <Field
            label="Created At"
            value={formatDateTime(dispatch.created_at)}
          />
          <Field
            label="Confirmed At"
            value={formatDateTime(dispatch.confirmed_at)}
          />
        </div>
      </section>

      <section style={styles.card}>
        <h2 style={styles.sectionTitle}>Actual Dispatch Items ({items.length})</h2>

        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Line</th>
                <th style={styles.th}>Item Description</th>
                <th style={styles.th}>Planned Qty.</th>
                <th style={styles.th}>Previously Dispatched</th>
                <th style={styles.th}>Actual Dispatch Qty.</th>
                <th style={styles.th}>Warehouse</th>
                <th style={styles.th}>Batch Allocation</th>
              </tr>
            </thead>

            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ ...styles.td, textAlign: "center" }}>
                    No Actual Dispatch items found.
                  </td>
                </tr>
              ) : (
                items.map((item, index) => {
                  const batches = Array.isArray(item.batch_allocations)
                    ? item.batch_allocations
                    : [];

                  return (
                    <tr key={item.id || index}>
                      <td style={styles.td}>
                        {item.item_seq_no ?? index + 1}
                      </td>

                      <td style={{ ...styles.td, minWidth: "180px" }}>
                        {item.item_description || "—"}
                      </td>

                      <td style={styles.td}>
                        {formatQuantity(item.planned_quantity_snapshot)}
                      </td>

                      <td style={styles.td}>
                        {formatQuantity(item.previously_dispatched_snapshot)}
                      </td>

                      <td style={{ ...styles.td, fontWeight: 700 }}>
                        {formatQuantity(item.actual_dispatch_quantity)}
                      </td>

                      <td style={styles.td}>
                        {item.warehouse_name ||
                          (item.warehouse_id
                            ? `Warehouse #${item.warehouse_id}`
                            : "—")}
                      </td>

                      <td style={styles.td}>
                        {batches.length === 0 ? (
                          <span style={styles.muted}>No batch allocation</span>
                        ) : (
                          <div style={{ display: "grid", gap: "6px" }}>
                            {batches.map((batch, batchIndex) => (
                              <div key={batch.id || batchIndex}>
                                <strong>{batch.batch_no || "—"}</strong>
                                {" — "}
                                {formatQuantity(batch.dispatch_quantity)}
                              </div>
                            ))}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section style={styles.card}>
        <h2 style={styles.sectionTitle}>Transport & Delivery Details</h2>

        <div style={styles.grid}>
          <Field
            label="Delivery Address"
            value={dispatch.delivery_address}
          />
          <Field
            label="Transporter ID"
            value={dispatch.transporter_bp_id}
          />
          <Field
            label="Transport Request ID"
            value={dispatch.transport_request_id}
          />
          <Field
            label="Vehicle Number"
            value={dispatch.vehicle_number}
          />
          <Field
            label="Driver Name"
            value={dispatch.driver_name}
          />
          <Field
            label="Driver Mobile"
            value={dispatch.driver_mobile}
          />
          <Field
            label="LR Number"
            value={dispatch.lr_number}
          />
          <Field
            label="LR Date"
            value={formatDate(dispatch.lr_date)}
          />
          <Field
            label="Remarks"
            value={dispatch.remarks}
          />
        </div>
      </section>

      {challan && (
        <section style={styles.card}>
          <h2 style={styles.sectionTitle}>Delivery Challan</h2>

          <div style={styles.grid}>
            <Field label="Challan No." value={challan.challan_no} />
            <Field
              label="Challan Date"
              value={formatDate(challan.challan_date)}
            />
            <Field label="Challan Status" value={formatStatus(challan.status_code)} />
            <Field label="Challan ID" value={challan.id} />
            <Field label="Remarks" value={challan.remarks} />
          </div>

          <div style={{ marginTop: "22px" }}>
            <h3 style={{ fontSize: "14px", margin: "0 0 12px" }}>
              Challan Items
            </h3>

            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Line</th>
                    <th style={styles.th}>Description</th>
                    <th style={styles.th}>Quantity</th>
                  </tr>
                </thead>

                <tbody>
                  {challanItems.length === 0 ? (
                    <tr>
                      <td colSpan={3} style={{ ...styles.td, textAlign: "center" }}>
                        No challan items returned.
                      </td>
                    </tr>
                  ) : (
                    challanItems.map((item, index) => (
                      <tr key={item.id || index}>
                        <td style={styles.td}>
                          {item.item_seq_no ?? index + 1}
                        </td>
                        <td style={styles.td}>
                          {item.item_description || "—"}
                        </td>
                        <td style={styles.td}>
                          {formatQuantity(
                            item.dispatch_quantity ??
                            item.quantity ??
                            item.actual_dispatch_quantity
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}

      <section style={styles.card}>
        <h2 style={styles.sectionTitle}>Confirmation Information</h2>

        <div style={styles.grid}>
          <Field label="Status" value={formatStatus(status)} />
          <Field label="Confirmed By" value={dispatch.confirmed_by} />
          <Field
            label="Confirmed At"
            value={formatDateTime(dispatch.confirmed_at)}
          />
          <Field label="Created By" value={dispatch.created_by} />
        </div>
      </section>
    </div>
  );
}
